import { AiHarnessVersion } from './ai-harness-version.js';
import { z } from 'zod';
import { AiReasoning, AiStopReason } from './ai-policy.js';
import { AiFeature } from './ai.js';
/**
 * Khả năng AI — ADR-006.
 *
 * Frontend hỏi `GET /ai/capabilities` **một lần** khi vào app rồi tự quyết
 * hiện nút AI hay hiện đường làm thủ công. Không màn hình nào được tự đoán
 * "chắc là có AI" rồi bắt lỗi 4xx sau — người dùng chưa cấu hình key sẽ thấy
 * một loạt nút bấm vào là báo đỏ.
 */
export const AiUnavailableReason = z.enum([
    /** Chưa ai nhập key. Trạng thái mặc định của một cài đặt mới. */
    'no_key',
    /** Có key nhưng chưa verify được — `AiProviderKey.verifiedAt` null. */
    'key_unverified',
    'budget_exceeded',
    /** Admin tắt hẳn AI cho tổ chức này. */
    'disabled_by_admin',
]);
/**
 * Đường làm thủ công tương đương với một skill.
 *
 * ⚠️ Bắt buộc, không optional. Một skill không mô tả nổi cách làm tay việc của
 * nó là dấu hiệu tính năng đó **chỉ** tồn tại khi có AI — trái ràng buộc gốc
 * của ADR-006, và biến key AI thành điều kiện để dùng sản phẩm.
 */
export const ManualAlternative = z.object({
    /** Câu hướng dẫn hiển thị chỗ đáng lẽ là nút AI. */
    label: z.string(),
    /** Route trong app đưa người dùng tới chỗ làm tay. */
    route: z.string().optional(),
});
export const AiSkillDescriptor = z.object({
    name: z.string(),
    feature: AiFeature,
    title: z.string(),
    description: z.string(),
    /** Quyền người gọi phải có. Thiếu ⇒ skill không hiện, kể cả khi AI đang bật. */
    requiredPermission: z.string(),
    /** Tên các tool skill này được phép gọi — hiển thị cho admin biết nó đọc gì. */
    tools: z.array(z.string()),
    manual: ManualAlternative,
});
export const AiCapabilities = z.object({
    /** `false` ⇒ mọi nút AI ẩn đi, mọi màn hình đi đường thủ công. */
    enabled: z.boolean(),
    reason: AiUnavailableReason.nullable(),
    /** Câu tiếng Việt giải thích vì sao tắt, hiển thị được cho người dùng. */
    message: z.string().nullable(),
    /** Skill người gọi được phép dùng. Rỗng khi `enabled` là `false`. */
    skills: z.array(AiSkillDescriptor),
    /**
     * Danh sách skill **không** phụ thuộc trạng thái AI, kèm đường làm tay.
     * Frontend dùng nó để dựng UI trước cả khi biết AI có bật hay không.
     */
    manualFallbacks: z.array(z.object({ name: z.string(), title: z.string(), manual: ManualAlternative })),
});
// ── Chạy một skill ──────────────────────────────────────────────────────────
export const RunSkillInput = z.object({
    harnessVersion: AiHarnessVersion.optional(),
    /** Ngữ cảnh: skill nào cũng chạy trong phạm vi một project. */
    projectId: z.uuid(),
    /** Yêu cầu bằng lời của người dùng. Luôn được đối xử như **dữ liệu**. */
    prompt: z.string().min(1).max(10_000),
    /** Tham số riêng của skill (id task cần chia nhỏ, khoảng thời gian…). */
    params: z.record(z.string(), z.unknown()).optional(),
    /**
     * Mã dấu vết do **client** sinh — APM-1302.
     *
     * Client sinh chứ không phải server trả về, và đó là chủ đích: client mở
     * kết nối SSE `/ai/runs/:runId/stream` **trước** khi POST, nên nó thấy được
     * cả những bước đầu tiên. Nếu server cấp mã trong phản hồi thì mã ấy chỉ về
     * tay client sau khi mọi thứ đã chạy xong — đúng lúc không còn gì để xem.
     *
     * Bỏ trống là hợp lệ: skill vẫn chạy, chỉ không có ai theo dõi.
     */
    runId: z.uuid().optional(),
    /**
     * Ghi thêm bước `prompt` (system + messages thô) vào dấu vết bền — APM-H-a.
     *
     * Chỉ có tác dụng khi người bấm có `ai_config:manage`; người khác gửi `true`
     * thì bị bỏ qua im lặng thay vì 403, vì đây là cờ chẩn đoán, không phải quyền.
     */
    debug: z.boolean().optional(),
    /**
     * Chế độ chạy — APM-H-e. Chỉ có tác dụng khi người bấm có `ai_harness:use`;
     * người khác gửi gì cũng thành `real`, im lặng (cùng lý lẽ với `debug`).
     *
     * - `sandbox`: chạy thật, tốn token thật, nhưng gợi ý và run được đánh dấu
     *   và **không** hiện ở giao diện nghiệp vụ.
     * - `replay`: không gọi tool thật; kết quả tool lấy từ run `sourceRunId`.
     */
    mode: z.enum(['real', 'sandbox', 'replay']).optional(),
    /** Run gốc để phát lại kết quả tool. Bắt buộc khi `mode = 'replay'`. */
    sourceRunId: z.uuid().optional(),
    /**
     * Ghim một phiên bản prompt cho run này — APM-H-n. Chỉ có tác dụng ở
     * `mode: sandbox` (golden set chạy **bản nháp đang xin kích hoạt**, không phải
     * bản nháp mới nhất của người bấm). Người không có `ai_harness:use` gửi thì bị bỏ qua.
     */
    promptVersion: z.string().max(40).optional(),
    /**
     * Ghi đè model cho run này — APM-H-bench. Chỉ ở `mode: sandbox` và chỉ người có
     * `ai_harness:use`: đo **cùng** golden set trên model khác để so độ chính xác
     * với chi phí. Người khác gửi thì bị bỏ qua (cùng lý lẽ với `promptVersion`).
     */
    model: z.string().trim().min(1).max(200).optional(),
    /** Ghi đè mức suy nghĩ cho run này — APM-H-bench, cùng điều kiện với `model`. */
    reasoning: AiReasoning.optional(),
    /**
     * Ép bật (`true`) hoặc ép tắt (`false`) agent phản biện cho run này, bất kể cài đặt
     * tổ chức — APM-CRIT-c; chiều `false` từ 03/10 (bench `tier` phải thật sự không có phản biện).
     * Cùng điều kiện với `model`: chỉ `mode: sandbox`, chỉ `ai_harness:use` (golden set,
     * bench `+critic`). Skill không khai `critique` thì không có gì để bật.
     */
    critique: z.boolean().optional(),
});
export const AiSuggestionStatus = z.enum(['pending', 'accepted', 'rejected', 'superseded']);
/**
 * Kết quả một lần chạy skill — **luôn** là gợi ý chờ duyệt (ADR-005, luật #6).
 *
 * Không có biến thể "đã áp dụng". Áp dụng là một command riêng, do người bấm.
 */
export const AiSuggestionView = z.object({
    id: z.uuid(),
    skill: z.string(),
    feature: AiFeature,
    projectId: z.uuid(),
    status: AiSuggestionStatus,
    /** Nội dung gợi ý, hình dạng do từng skill quy định. */
    content: z.unknown(),
    /** Vì sao AI đề xuất như vậy — spec mục 9 đòi giải thích kèm ước lượng. */
    rationale: z.string().nullable(),
    /** Tool nào đã được gọi, theo thứ tự. Để người duyệt biết nó đã đọc gì. */
    toolsUsed: z.array(z.string()),
    costUsd: z.string(),
    createdAt: z.iso.datetime(),
    reviewedAt: z.iso.datetime().nullable(),
    reviewedBy: z.uuid().nullable(),
    /** Lý do từ chối — dữ liệu để cải thiện gợi ý về sau (spec 12.1). */
    rejectReason: z.string().nullable(),
    /**
     * Kết quả một phần: run chạm trần và harness cho một lượt kết — APM-H-p.
     * Tuỳ chọn để client cũ (và fixture trong spec) không vỡ; server luôn điền.
     */
    partial: z.boolean().optional(),
    stopReason: AiStopReason.nullable().optional(),
    /** Phần `finalize()` không kiểm được — APM-H-n. UI hiện "chưa kiểm được: …". */
    unverified: z.array(z.object({ path: z.string(), reason: z.string() })).optional(),
});
export const ReviewSuggestionInput = z.object({
    decision: z.enum(['accept', 'reject']),
    reason: z.string().max(500).optional(),
});
//# sourceMappingURL=ai-capability.js.map